/**
 * English catalog — the Agent Studio (assistant.agents.*).
 *
 * Split from enAssistant like the other catalogs, keeping every catalog file
 * under the 500-line fileSize ratchet. Spread into `en` in ./en.ts.
 */
export const enAssistantAgents = {
  // ── The tab ───────────────────────────────────────────────────────────
  'assistant.agents.tab.heading': 'Agents',
  'assistant.agents.tab.subtitle': 'Named agents that work inside your business — each with its own job, its own tools, and a ceiling it cannot raise.',
  'assistant.agents.tab.new': 'New agent',
  'assistant.agents.tab.emptyTitle': 'No agents yet',
  'assistant.agents.tab.emptyHint': 'Start from a template or a blank page. An agent ships switched off; it runs only after the sandbox passes.',
  'assistant.agents.tab.loadFailed': 'The agent list could not be loaded.',
  'assistant.agents.tab.waitingHostHint': 'They start the next time your AI checks in through the plugin.',

  // ── Roster (chat tab) ─────────────────────────────────────────────────
  'assistant.agents.roster.label': 'Agents',
  'assistant.agents.roster.assistant': 'Assistant',
  'assistant.agents.roster.loading': 'Loading agents…',
  'assistant.agents.roster.empty': 'No agents yet.',
  'assistant.agents.roster.waitingForHost': '{count} waiting for your AI',
  'assistant.agents.roster.new': 'New agent',
  'assistant.agents.roster.manage': 'Manage',

  // ── Statuses ──────────────────────────────────────────────────────────
  'assistant.agents.status.disabled': 'Off',
  'assistant.agents.status.enabled': 'Ready',
  'assistant.agents.status.paused': 'Paused',
  'assistant.agents.status.pausedByCap': 'Paused — daily cap',
  'assistant.agents.run.queued': 'Queued',
  'assistant.agents.run.queuedForHost': 'Waiting for your AI',
  'assistant.agents.run.running': 'Working',
  'assistant.agents.run.runningOnHost': 'Working in your AI',
  'assistant.agents.run.waiting': 'Waiting for your approval',
  'assistant.agents.run.succeeded': 'Done',
  'assistant.agents.run.failed': 'Failed',
  'assistant.agents.run.cancelled': 'Cancelled',
  'assistant.agents.run.expired': 'Expired',
  'assistant.agents.run.skipped': 'Skipped',

  // ── Brains and ceilings ───────────────────────────────────────────────
  'assistant.agents.brain.host': 'Your AI',
  'assistant.agents.brain.hostExplain': 'Runs inside the AI you connected through the plugin. Nothing is billed to BuzzBit X.',
  'assistant.agents.brain.byo': 'Your key',
  'assistant.agents.brain.byoExplain': 'Runs on the server with the model key you added. Starts as soon as it is asked.',
  'assistant.agents.brain.byoNoKey': 'Needs a model key — add one in the assistant panel first. Without it every run fails.',
  'assistant.agents.brain.deterministic': 'No model',
  'assistant.agents.brain.deterministicExplain': 'A fixed routine, run by code. No model, no key, the same rails.',
  'assistant.agents.ceiling.l0': 'Read only',
  'assistant.agents.ceiling.l0Explain': 'Looks things up and reports. Changes nothing.',
  'assistant.agents.ceiling.l1': 'Drafts',
  'assistant.agents.ceiling.l1Explain': 'Creates and edits things that are switched off. Nothing reaches a customer.',
  'assistant.agents.ceiling.l2': 'One person',
  'assistant.agents.ceiling.l2Explain': 'May reach exactly one person — a booking, a single message.',

  // ── Form ──────────────────────────────────────────────────────────────
  'assistant.agents.form.name': 'Name',
  'assistant.agents.form.namePlaceholder': 'Booker, Follow-up, Analyst…',
  'assistant.agents.form.color': 'Colour',
  'assistant.agents.form.jobPrompt': 'Its job',
  'assistant.agents.form.jobPromptPlaceholder': 'What this agent is for, what it should look at, what it should draft, and when it should leave things to you.',
  'assistant.agents.form.jobPromptHint': 'Plain instructions. What it may do is decided by its tools and ceiling, not by these words.',
  'assistant.agents.form.capabilities': 'Tools',
  'assistant.agents.form.capabilitiesHint': 'Tick what it may use. Anything not ticked is refused at run time, whatever it asks for.',
  'assistant.agents.form.ceiling': 'How far it may go',
  'assistant.agents.form.ceilingHint': 'Its own ceiling, never above the workspace’s.',
  'assistant.agents.form.l3Note': 'Sending to many, or switching something live, always stops for your approval — for every agent, at every ceiling.',
  'assistant.agents.form.dailyRunCap': 'Runs per day',
  'assistant.agents.form.dailyRunCapHint': 'At the cap it pauses until midnight and tells you once.',
  'assistant.agents.form.budget': 'Tool calls per run',
  'assistant.agents.form.budgetHint': 'A run stops when it has used these.',
  'assistant.agents.form.brain': 'Who thinks for it',
  'assistant.agents.form.routine': 'Routine',
  'assistant.agents.form.routineNeeds': 'Needs: {tools}',
  'assistant.agents.form.triggers': 'When it runs',
  'assistant.agents.form.triggersExplain': 'It always runs when you ask (here, or with @name in the chat), when the assistant hands it work, or when your AI checks in. A schedule or an event starts runs on their own.',
  'assistant.agents.triggers.hostNote': 'This agent thinks with your AI, so a scheduled or event run waits until your AI checks in or is nudged. A run nobody picks up expires after a day.',
  'assistant.agents.triggers.serverNote': 'This agent runs on the server, so a scheduled or event run starts at once, within its daily cap.',
  'assistant.agents.triggers.none': 'No schedules or events yet.',
  'assistant.agents.triggers.remove': 'Remove',
  'assistant.agents.triggers.kind.schedule': 'On a schedule',
  'assistant.agents.triggers.kind.event': 'When something happens',
  'assistant.agents.triggers.when': 'When',
  'assistant.agents.triggers.preset.daily': 'Every day at 09:00',
  'assistant.agents.triggers.preset.weekdays': 'Weekdays at 09:00',
  'assistant.agents.triggers.preset.weekly': 'Mondays at 09:00',
  'assistant.agents.triggers.preset.hourly': 'Every hour',
  'assistant.agents.triggers.preset.custom': 'A cron expression of my own',
  'assistant.agents.triggers.cron': 'Cron expression',
  'assistant.agents.triggers.cronExample': '0 9 * * 1-5',
  'assistant.agents.triggers.cronHint': 'Five fields (minute, hour, day of month, month, day of week) in your workspace timezone, at most once every fifteen minutes. It fires within five minutes after the time.',
  'assistant.agents.triggers.cronAt': 'On the schedule {cron}',
  'assistant.agents.triggers.cronProblem': 'A schedule is a five-field cron expression.',
  'assistant.agents.triggers.event': 'Event',
  'assistant.agents.triggers.eventOn': 'When {event}',
  'assistant.agents.triggers.eventWithFilter': 'When {event} ({filter})',
  'assistant.agents.triggers.filterKey': 'Only when this field…',
  'assistant.agents.triggers.filterValue': '…equals',
  'assistant.agents.triggers.filterHint': 'Optional. A field of the event and the value it must have, for example tier = gold.',
  'assistant.agents.triggers.goal': 'What to do',
  'assistant.agents.triggers.goalHint': 'The goal of the run this starts, in your words. The job description still applies.',
  'assistant.agents.triggers.goalProblem': 'Say what the run should do, in three to five hundred characters.',
  'assistant.agents.triggers.addSchedule': 'Add the schedule',
  'assistant.agents.triggers.addEvent': 'Add the event',
  'assistant.agents.firstRun.title': 'The first scheduled run',
  'assistant.agents.firstRun.explain': 'What each tool it holds would do {when}, ruled at this agent\'s ceiling. The sandbox checks the same rulings against the real code before it can be switched on.',
  'assistant.agents.firstRun.jitter': 'It fires within five minutes after the time, so many agents never wake at the same second.',
  'assistant.agents.event.booking.scheduled': 'a booking was made',
  'assistant.agents.event.booking.rescheduled': 'a booking was moved',
  'assistant.agents.event.booking.cancelled': 'a booking was cancelled',
  'assistant.agents.event.booking.no_show': 'a customer did not show up',
  'assistant.agents.event.booking.completed': 'a booking was completed',
  'assistant.agents.event.cart.abandoned': 'a cart or checkout was abandoned',
  'assistant.agents.event.order.created': 'an order came in',
  'assistant.agents.event.order.paid': 'an order was paid',
  'assistant.agents.event.order.fulfilled': 'an order was fulfilled',
  'assistant.agents.event.order.cancelled': 'an order was cancelled',
  'assistant.agents.event.customer.created': 'a new customer arrived',
  'assistant.agents.event.saas.payment_failed': 'a subscription payment failed',
  'assistant.agents.event.saas.subscription_cancelled': 'a subscription was cancelled',
  'assistant.agents.event.saas.trial_ending': 'a trial is about to end',
  'assistant.agents.trigger.MANUAL': 'on request',
  'assistant.agents.trigger.SCHEDULE': 'on schedule',
  'assistant.agents.trigger.EVENT': 'from an event',
  'assistant.agents.trigger.MENTION': 'from a mention',
  'assistant.agents.trigger.HANDOFF': 'from a handoff',

  // ── Capability picker ─────────────────────────────────────────────────
  'assistant.agents.picker.search': 'Find a tool…',
  'assistant.agents.picker.count': '{count} tools',
  'assistant.agents.picker.all': 'All',
  'assistant.agents.picker.none': 'None',
  'assistant.agents.picker.neverGrantable': 'never for agents',
  'assistant.agents.picker.orchestration': 'the assistant’s own',
  'assistant.agents.picker.aboveCeiling': 'above this ceiling — will be refused',

  // ── Lint ──────────────────────────────────────────────────────────────
  'assistant.agents.lint.checking': 'Checking the job…',
  'assistant.agents.lint.clean': 'The job reads clean.',
  'assistant.agents.lint.warningsOnly': '{count} thing(s) worth a look — nothing blocks.',
  'assistant.agents.lint.blocked': '{count} thing(s) block enabling this agent.',
  'assistant.agents.lint.suggestGrant': 'Grant: {tools}',

  // ── Sandbox ───────────────────────────────────────────────────────────
  'assistant.agents.sandbox.heading': 'Sandbox',
  'assistant.agents.sandbox.explain': 'Plays the job against simulated tools: the plan, an injection, a tool outside its set, its ceiling, its budget. Nothing is sent or created. It must pass before the agent can be enabled.',
  'assistant.agents.sandbox.run': 'Run the sandbox',
  'assistant.agents.sandbox.runReal': 'Run with real reads',
  'assistant.agents.sandbox.running': 'Running…',
  'assistant.agents.sandbox.runFailed': 'The sandbox could not run.',
  'assistant.agents.sandbox.passed': 'Sandbox passed',
  'assistant.agents.sandbox.failed': 'Sandbox failed',
  'assistant.agents.sandbox.notYet': 'not yet sandboxed',
  'assistant.agents.sandbox.passedAt': 'Passed {at}',
  'assistant.agents.sandbox.passedLine': 'The rails held. This agent can be enabled.',
  'assistant.agents.sandbox.failedLine': 'Something got through, or the plan cannot run as written. It cannot be enabled until this passes.',
  'assistant.agents.sandbox.meta': '{mode} · ceiling {ceiling}',
  'assistant.agents.sandbox.planHeading': 'What its plan would do',
  'assistant.agents.sandbox.scenariosHeading': 'Scenarios',
  'assistant.agents.sandbox.scenarioPassed': 'held',
  'assistant.agents.sandbox.scenarioFailed': 'failed',
  'assistant.agents.sandbox.ruling.runs': 'runs',
  'assistant.agents.sandbox.ruling.parks': 'asks you',
  'assistant.agents.sandbox.ruling.refused': 'refused',
  'assistant.agents.sandbox.brain.host': 'Its model runs in your AI, so the model layer was not played here — the rails were.',
  'assistant.agents.sandbox.brain.noKey': 'No model key here, so the model layer was not played — the rails were.',
  'assistant.agents.sandbox.brain.noModel': 'A routine has no model; the rails were played.',
  'assistant.agents.sandbox.brain.skipped': 'The model layer was skipped; the rails were played.',
  'assistant.agents.sandbox.brain.persuaded': 'Its own model was talked into trying something it must not — and the rails refused it anyway.',
  'assistant.agents.sandbox.brain.held': 'Its own model stayed on task.',

  // ── The gate ──────────────────────────────────────────────────────────
  'assistant.agents.gate.enable': 'Enable',
  'assistant.agents.gate.enabling': 'Enabling…',
  'assistant.agents.gate.sandboxRequired': 'Run the sandbox first — it has to pass since the last change.',
  'assistant.agents.gate.lintBlocks': 'Fix what the lint flags first.',
  'assistant.agents.gate.failed': 'That switch did not take.',

  // ── Wizard ────────────────────────────────────────────────────────────
  'assistant.agents.wizard.title': 'New agent',
  'assistant.agents.wizard.close': 'Close',
  'assistant.agents.wizard.steps': 'Steps',
  'assistant.agents.wizard.step.start': 'Start',
  'assistant.agents.wizard.step.identity': 'Job',
  'assistant.agents.wizard.step.capabilities': 'Tools',
  'assistant.agents.wizard.step.autonomy': 'Limits',
  'assistant.agents.wizard.step.review': 'Review',
  'assistant.agents.wizard.startHint': 'Start from a template that already passes the lint and the sandbox, or from a blank page.',
  'assistant.agents.wizard.noTemplates': 'Templates are on their way. Start blank for now.',
  'assistant.agents.wizard.startBlank': 'Start blank',
  'assistant.agents.wizard.customerFacing': 'reaches customers',
  'assistant.agents.wizard.customerFacingNote': 'This agent can reach one customer at a time — a booking or a single message. Every send goes through the same consent checks as anything you send yourself, it stops for you whenever a message would reach many, and its runs are on the ledger under its name.',
  'assistant.agents.wizard.back': 'Back',
  'assistant.agents.wizard.next': 'Next',
  'assistant.agents.wizard.create': 'Create agent',
  'assistant.agents.wizard.creating': 'Creating…',
  'assistant.agents.wizard.createFailed': 'The agent could not be created.',
  'assistant.agents.wizard.nameTaken': 'An agent with that name already exists here.',
  'assistant.agents.wizard.reviewNote': 'It will be created switched off. Next: the sandbox, then Enable.',
  'assistant.agents.wizard.createdLine': '{name} exists and is switched off. Run the sandbox to see the rails hold, then enable it.',
  'assistant.agents.wizard.keepDisabled': 'Keep it off for now',

  // ── Detail ────────────────────────────────────────────────────────────
  'assistant.agents.detail.loading': 'Loading…',
  'assistant.agents.detail.back': '← All agents',
  'assistant.agents.detail.pause': 'Pause',
  'assistant.agents.detail.disable': 'Switch off',
  'assistant.agents.detail.editHeading': 'The job, tools and limits',
  'assistant.agents.detail.edit': 'Edit',
  'assistant.agents.detail.cancelEdit': 'Cancel',
  'assistant.agents.detail.save': 'Save',
  'assistant.agents.detail.saving': 'Saving…',
  'assistant.agents.detail.saved': 'Saved.',
  'assistant.agents.detail.savedNeedsSandbox': 'Saved. That changed what it would do, so it is off until the sandbox passes again.',
  'assistant.agents.detail.saveFailed': 'That edit was not saved.',
  'assistant.agents.detail.editWarning': 'Changing the job, the tools, the ceiling or the brain switches the agent off until the sandbox passes again.',
  'assistant.agents.detail.delete': 'Delete agent',
  'assistant.agents.detail.deleting': 'Deleting…',
  'assistant.agents.detail.deleteConfirm': 'Delete this agent? Its runs and their ledger rows stay; the agent itself is gone.',
  'assistant.agents.detail.deleteFailed': 'The agent could not be deleted.',

  // ── Runs ──────────────────────────────────────────────────────────────
  'assistant.agents.runs.heading': 'Runs',
  'assistant.agents.runs.loading': 'Loading runs…',
  'assistant.agents.runs.empty': 'No runs yet.',
  'assistant.agents.runs.progress': '{used}/{budget} calls',
  'assistant.agents.runs.step': 'step {step}',
  'assistant.agents.runs.cancel': 'Cancel',
  'assistant.agents.runs.cancelling': 'Cancelling…',
  'assistant.agents.runs.waitingHostHint': 'Starts when your AI checks in through the plugin (run_agent).',
  'assistant.agents.runs.fromHandoff': 'from a handoff',
  'assistant.agents.runs.onHost': 'in {host}',
  'assistant.agents.run.runNow': 'Run now',
  'assistant.agents.run.starting': 'Starting…',
  'assistant.agents.run.started': 'Queued. It runs from here.',
  'assistant.agents.run.startedForHost': 'Queued for your AI — it starts at the next check-in.',
  'assistant.agents.run.refused': 'That run was refused.',
  'assistant.agents.run.refusedNotEnabled': 'This agent is switched off. Enable it first.',
  'assistant.agents.run.refusedCap': 'It has used its runs for today. It resumes after midnight.',
  'assistant.agents.run.refusedBusy': 'It is in the middle of a run. This one will wait its turn.',

  // ── Thread (chat tab) ─────────────────────────────────────────────────
  'assistant.agents.thread.back': '← Assistant',
  'assistant.agents.thread.manage': 'Manage',
  'assistant.agents.thread.missing': 'That agent is not here any more.',
  'assistant.agents.thread.placeholder': 'Tell {name} what to do…',
  'assistant.agents.thread.disabledNote': 'This agent is switched off. It will not run until it is enabled in the Studio.',
  'assistant.agents.thread.cappedNote': 'This agent has used its runs for today. It resumes after midnight.',

  // ── @mentions ─────────────────────────────────────────────────────────
  'assistant.agents.mention.label': 'Agents',
  'assistant.agents.mention.notEnabled': 'off',
  'assistant.agents.mention.needsReason': 'Tell {name} what to do after its name — a few words at least.',
  'assistant.agents.mention.handedOff': 'Handed to {name} — queued to run.',
  'assistant.agents.mention.handedOffHost': 'Handed to {name} — it runs when your AI checks in.',
  'assistant.agents.mention.open': 'Open {name}',
  'assistant.agents.mention.refused': '{name} could not take that: {reason}',

  // ── Meter and live ────────────────────────────────────────────────────
  'assistant.agents.meter.label': '{used} of {cap} runs today',
  'assistant.agents.meter.title': '{used} of {cap} runs today',
  'assistant.agents.live.done': '{name} finished a run.',
  'assistant.agents.live.failed': '{name} could not finish a run.',
  'assistant.agents.live.waiting': '{name} is waiting for your approval.',
  'assistant.agents.live.capped': '{name} has used its runs for today.',
  'assistant.agents.live.queuedForHost': '{name} has work waiting for your AI.',

  // ── Domains ───────────────────────────────────────────────────────────
  'assistant.agents.domain.crm': 'Customers & leads',
  'assistant.agents.domain.email': 'Email, campaigns & flows',
  'assistant.agents.domain.messaging': 'WhatsApp, Messenger & Instagram',
  'assistant.agents.domain.bookings': 'Bookings',
  'assistant.agents.domain.boards': 'Boards',
  'assistant.agents.domain.popups_funnels': 'Popups, funnels & pages',
  'assistant.agents.domain.ads': 'Meta ads',
  'assistant.agents.domain.content': 'Content & social',
  'assistant.agents.domain.automations': 'Automations',
  'assistant.agents.domain.signals': 'Signals & triggers',
  'assistant.agents.domain.analytics': 'Analytics & finance',
  'assistant.agents.domain.commerce': 'Orders, products & support',
  'assistant.agents.domain.knowledge': 'Knowledge',
  'assistant.agents.domain.research': 'Research',
  'assistant.agents.domain.integrations': 'Integrations',
  'assistant.agents.domain.workspace': 'Workspace & billing',
  'assistant.agents.domain.assistant': 'Assistant',

  // ── The plan's allowance (A8) ─────────────────────────────────────────
  'assistant.agents.allowance.heading': 'Agents',
  'assistant.agents.allowance.loading': 'Loading the agents\' allowance…',
  'assistant.agents.allowance.plan': 'Your {plan} plan',
  'assistant.agents.allowance.planTrial': 'Your trial, measured as {plan}',
  'assistant.agents.allowance.count': '{used}/{limit}',
  'assistant.agents.allowance.agents': 'Agents',
  'assistant.agents.allowance.onDuty': 'On duty',
  'assistant.agents.allowance.runsToday': 'Runs today',
  'assistant.agents.allowance.upgrade': 'Change plan',
  'assistant.agents.allowance.noRuns': 'Agents run on a paid plan or a trial. Yours can be made and sandboxed, not switched on.',
  'assistant.agents.allowance.spent': 'Today\'s runs are used up. Agents that ask to run pause until midnight UTC; nothing is lost.',
  'assistant.agents.allowance.near': 'Most of today\'s runs have started. At the limit, agents pause until midnight UTC.',
  'assistant.agents.pausedReason.dailyCap': 'Paused: its own daily cap. It resumes at midnight UTC.',
  'assistant.agents.pausedReason.planAllowance': 'Paused: the workspace has started all the runs its plan allows today. It resumes at midnight UTC.',
  'assistant.agents.pausedReason.planRequired': 'Paused: agents run on a paid plan or a trial.',
  'assistant.agents.pausedReason.owner': 'Paused by you.',
  'assistant.agents.packs.heading': 'Start with a team, not a blank page',
  'assistant.agents.packs.explain': 'Each pack makes a few agents that work together. They arrive switched off, and each still has to pass its own sandbox before it can do anything.',
  'assistant.agents.packs.forYou': 'fits your business',
  'assistant.agents.packs.install': 'Add these',
  'assistant.agents.packs.installing': 'Adding…',
  'assistant.agents.packs.installed': '{count} added, all switched off. Open one to run its sandbox.',
  'assistant.agents.packs.installedPartly': '{count} added, all switched off. Not added: {skipped}.',
  'assistant.agents.packs.alreadyThere': 'You already have these agents. Nothing was added.',
  'assistant.agents.packs.noRoom': 'Your plan is holding as many agents as it can. Remove one, or change plan, to add a pack.',
  'assistant.agents.packs.failed': 'That did not go through. Nothing was added.',
  'assistant.agents.notes.heading': 'What it knows',
  'assistant.agents.notes.explain': 'Things you have told this agent, and things it noticed and asked to remember. A note is a fact about your business, never an instruction — it can never change what the agent is allowed to do.',
  'assistant.agents.notes.proposedHeading': '{count} waiting for you',
  'assistant.agents.notes.keptHeading': 'What it goes in with',
  'assistant.agents.notes.empty': 'Nothing yet. Tell it something it should always know.',
  'assistant.agents.notes.keep': 'Keep',
  'assistant.agents.notes.dismiss': 'Throw away',
  'assistant.agents.notes.add': 'Add',
  'assistant.agents.notes.placeholder': 'Something this agent should always know…',
  'assistant.agents.notes.fromYou': 'you told it',
  'assistant.agents.notes.fromAgent': 'it noticed, you kept it',
  'assistant.agents.notes.failed': 'That did not save. Nothing changed.',
  'assistant.agents.kpi.heading': 'What it is for',
  'assistant.agents.kpi.reading': '{metric}: {actual} of {target} in the last {window}',
  'assistant.agents.kpi.met': 'On target.',
  'assistant.agents.kpi.missed': 'Behind target.',
  'assistant.agents.kpi.none': 'No goal set. Without one, nobody can say whether this agent is helping.',
  'assistant.agents.kpi.metric.runs_completed': 'runs finished',
  'assistant.agents.kpi.metric.work_created': 'things made',
  'assistant.agents.kpi.metric.asked_for_help': 'times it asked you',
  'assistant.agents.kpi.metric.approved_by_owner': 'approvals you granted',
  'assistant.agents.detail.outcomes': 'Last 7 days: {succeeded} succeeded · {failed} failed · {cancelled} stopped early',
} as const;
